import { FormEvent, useState, ChangeEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { useNavigate } from "react-router-dom";
import { salesAdminForgotPassword } from "../../redux/thunk";
import LoadingSpinner from "../../components/elements/LoadingSpinner";

interface Errors {
  email?: string;
}

const SalesOfficerForgotPassword = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Errors>({});
  const { status } = useAppSelector((store: RootState) => store.auth);
  const [inputValue, setInputValue] = useState({
    email: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value.trim() !== "") {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
    setInputValue((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors: Errors = {};

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputValue.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    if (validateForm()) {
      try {
        const result = await dispatch(
          salesAdminForgotPassword({ email: inputValue.email.toLowerCase() })
        );

        if (result.type === "salesAdminForgotPassword/fulfilled") {
          // Store email in sessionStorage for reset password page
          sessionStorage.setItem("resetEmail", inputValue.email.toLowerCase());
          navigate("/reset-password", { replace: true });
        }
      } catch (error) {
        console.error("Forgot password request failed:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-[100vh]">
      <div className="px-4 lg:px-14 w-full lg:w-[45%] mt-10 lg:mt-[10rem]">
        <div className="flex items-center justify-center">
          <img
            src="assets/logo.png"
            alt="logo"
            className="pb-15 self-center w-[6rem]"
          />
        </div>
        <div className="flex items-center justify-center flex-col">
          <h1 className="font-bold text-xl mb-3 mt-9">Forgot Password</h1>
          <p className="text-gray-600 mb-6 text-center">
            Enter your email address and we'll send you an OTP to reset your
            password
          </p>
        </div>
        <form className="px-4 lg:px-14 mb-[5rem]" onSubmit={handleSubmit}>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Email</label>
            <input
              name="email"
              value={inputValue.email}
              onChange={handleInputChange}
              type="text"
              placeholder="Enter your email address"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            {errors.email && (
              <p className="text-red-500 text-xs">{errors.email}</p>
            )}
          </div>

          <button
            className="bg-secondary text-white w-[100%] text-base font-semibold p-2 lg:p-3 rounded-md shadow-sm mt-8 lg:mt-10 mb-6 transition-all duration-500 hover:border-secondary hover:border-2 hover:text-secondary hover:bg-transparent"
            type="submit"
          >
            {isLoading ? <LoadingSpinner /> : "Send OTP"}
          </button>
          <div className="text-center">
            <a href="/" className="text-green-600 hover:underline">
              Back to Login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesOfficerForgotPassword;
